import React, { HTMLProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toPrice } from '../../../utils/parsers';
import { isPensionCalculatedTogether } from '../../../utils/functions';
import { Account, AccountType, PensionContext, PensionParameters } from '../../../contexts/pension-robot/PensionContext';
import { useWindowBreakpoints } from '../../../hooks/useWindowBreakpoints';
import { Button } from '../../general/Button';

export const SUPPLEMENT_PER_MONTH_DIVIDER = 12 * 20;

type Totals = { totalAOW: number; totalEmployeePension: number; totalSupplement: number };

export const Cocktail: React.FC<{ isColorGoal?: boolean } & HTMLProps<HTMLDivElement>> = ({
    isColorGoal = false,
    className,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { general, personalInfo, expensesInfo, pensionInfo, supplementInfo },
    } = useContext(PensionContext);
    const breakpoints = useWindowBreakpoints();
    const totalAOW =
        aow(pensionInfo, personalInfo, general.taxPercentage ?? 0) +
        aow(pensionInfo, personalInfo, general.taxPercentage ?? 0, true);
    const totalEmployeePension =
        pension(pensionInfo, personalInfo, general.taxPercentage ?? 0) +
        pension(pensionInfo, personalInfo, general.taxPercentage ?? 0, true);
    const [displayStartPeriodPension, setDisplayStartPeriodPension] = useState<boolean>(false);

    const investments = supplement(supplementInfo, 'investment', false);
    const savings = supplement(supplementInfo, 'saving', false) + savingsResultPage(supplementInfo);

    const totalSupplement =
        Math.floor(investments / SUPPLEMENT_PER_MONTH_DIVIDER / 100) * 100 +
        Math.floor(savings / SUPPLEMENT_PER_MONTH_DIVIDER / 100) * 100;

    const total = totalAOW + totalEmployeePension + totalSupplement;
    const graphLimit = Math.ceil((Math.max(total, expensesInfo?.goal ?? 0) * 1.2) / 100000) * 100000;

    return (
        <>
        <div
            className={`${className} text-sm sm:text-base w-full md:w-1/2 bg-white dark:bg-bnd-black-2 dark:border dark:border-gray-500 dark:shadow-none shadow-xl xl:rounded-lg`}
        >
            <div className='mx-4 mt-4 md:mx-8 md:mt-8 xl:mb-4'>
                <div className="mb-8">
                    <span className="font-black text-2xl">{t('pension-cocktail-fill-screen-cocktail-title')}</span>
                </div>
                {personalInfo.isCalculatedTogether && <CocktailSelectPeriodButtons 
                    displayStartPeriodPension={displayStartPeriodPension} 
                    setDisplayStartPeriodPension={setDisplayStartPeriodPension}
                />}
                <div className="grid grid-cols-2 gap-8">
                    <CocktailLegend
                        totals={{ totalAOW, totalEmployeePension, totalSupplement }}
                        total={total}
                        className="pt-2 pb-2"
                        isColorGoal={isColorGoal}
                    />
                    <div className="">
                        <div className="relative h-full grid grid-cols-8">
                            <CocktailChart
                                total={total}
                                totals={{ totalAOW, totalEmployeePension, totalSupplement }}
                                graphLimit={graphLimit}
                                className="pt-2 pb-2"
                                isColorGoal={isColorGoal}
                            />
                            {!(breakpoints.isMobile || breakpoints.isTablet) && <CocktailAxis graphLimit={graphLimit} axesCount={4} />}
                            {(expensesInfo?.goal ?? 0) > 0 && (
                                <GoalIndication
                                    isColorGoal={isColorGoal}
                                    goal={expensesInfo?.goal ?? 0}
                                    total={total}
                                    graphLimit={graphLimit}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <ProgressBadge goal={expensesInfo?.goal} total={total} isColorGoal={isColorGoal} className="h-auto"/> 
        </div>
        </>
    );
};

const CocktailSelectPeriodButtons: React.FC<{displayStartPeriodPension: boolean, setDisplayStartPeriodPension: (val: boolean) => void}> 
= ({ displayStartPeriodPension, setDisplayStartPeriodPension }) => {
    const { t } = useTranslation('pension');

    return (
        <div className='flex flex-row gap-x-4 gap-y-2 flex-wrap mb-8'>
            <div>
                <Button
                    className="px-4 font-semibold"
                    tint={displayStartPeriodPension ? `green`: 'neutral-no-hover'} 
                    size="sm"
                    onClick={() => setDisplayStartPeriodPension(!displayStartPeriodPension)}
                >
                    {t('pension-cocktail-fill-screen-cocktail-btn')}
                </Button>
            </div>
            <div>
                <Button
                    className="px-4 h-10 font-semibold"
                    tint={displayStartPeriodPension ? `neutral-no-hover` : 'green'}
                    size="sm"
                    onClick={() => setDisplayStartPeriodPension(!displayStartPeriodPension)}
                >
                    {t('pension-cocktail-fill-screen-cocktail-btn-partner')}
                </Button>
            </div>
        </div>
    );
};

const CocktailChart: React.FC<
    {
        total: number;
        graphLimit: number;
        isColorGoal: boolean;
        totals: Totals;
    } & HTMLProps<HTMLDivElement>
> = ({ total, graphLimit, className, isColorGoal, totals: { totalAOW, totalEmployeePension, totalSupplement } }) => {
    const {
        parameters: { expensesInfo, supplementInfo },
    } = useContext(PensionContext);
    const shortage = Math.max(0, !!isColorGoal ? (expensesInfo?.goal ?? 0) - total : 0);

    return (
        <div className={`col-span-5 h-full ${className}`}>
            <div
                className="flex h-full rounded-lg border-4"
                style={{
                    borderColor: '#FAFAFA',
                    backgroundColor: '#FAFAFA',
                    boxShadow: '6px 6px 10px rgba(174, 174, 192, 0.2), -6px -6px 10px rgba(255, 255, 255, 0.7)',
                }}
            >
                <div className="justify-end items-stretch flex flex-col flex-1 shadow-inner-2 rounded-lg min-h-500">
                    {<div style={{ flex: `${graphLimit - total - shortage}` }} />}
                    {!!isColorGoal && shortage > 0 && (
                        <div className="rounded-lg shadow-inner-2 bg-bnd-red" style={{ flex: shortage }} />
                    )}
                    {totalSupplement > 0 && (
                        <div
                            className="mt-1 rounded-lg items-stretch flex flex-col overflow-hidden"
                            style={{ flex: totalSupplement }}
                        >
                            <div
                                className="bg-bnd-cyan-1 shadow-inner-2"
                                style={{
                                    flex: supplement(supplementInfo, 'investment', false) / SUPPLEMENT_PER_MONTH_DIVIDER,
                                }}
                            />
                            <div
                                className={`bg-bnd-cyan-2 ${
                                    supplement(supplementInfo, 'investment', false) === 0
                                        ? 'shadow-inner-2'
                                        : 'shadow-inner-left'
                                }`}
                                style={{ flex: supplement(supplementInfo, 'saving', false) / SUPPLEMENT_PER_MONTH_DIVIDER }}
                            />
                        </div>
                    )}
                    {totalEmployeePension > 0 && (
                        <div
                            className="rounded-lg shadow-inner-2 bg-bnd-blue mt-1"
                            style={{ flex: totalEmployeePension }}
                        />
                    )}
                    {totalAOW > 0 && (
                        <div className="rounded-lg shadow-inner-2 bg-bnd-yellow mt-1" style={{ flex: totalAOW }} />
                    )}
                </div>
            </div>
        </div>
    );
};

const CocktailLegend: React.FC<
    {
        total: number;
        isColorGoal: boolean;
        totals: Totals;
    } & HTMLProps<HTMLDivElement>
> = ({ total, className, isColorGoal, totals: { totalAOW, totalEmployeePension } }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { general, pensionInfo, expensesInfo, personalInfo, supplementInfo },
    } = useContext(PensionContext);
    const generalPM = t('general-pm');
    const goalDelta = (expensesInfo?.goal ?? 0) - total;
    const savingsResultPageRes = savingsResultPage(supplementInfo);
    const investmentsResultPageRes = investmentsResultPage(supplementInfo);
    const supplements = yearSpacePensionAmount(supplementInfo) + supplement(supplementInfo, 'saving', false) + supplement(supplementInfo, 'investment', false);
    const otherIncomes = 0; // @@TODO: derive other incomes see ROBO-410
    // Add partner saving and investment accounts total here

    return (
        <div className={className}>
            {!isColorGoal && (
                <LegendItemDouble
                    title={t('pension-cocktail-fill-screen-cocktail-legend-surplus')}
                    className="mb-5"
                    colorLeft="bg-bnd-red"
                    colorRight="bg-bnd-green"
                />
            )}
            {isColorGoal && goalDelta > 0 && (
                <LegendItem
                    title={t('pension-cocktail-fill-screen-shortage')}
                    amount={goalDelta}
                    className="mb-5"
                    color="bg-bnd-red"
                    generalPM={generalPM}
                    notes={[]}
                />
            )}
            {isColorGoal && goalDelta < 0 && (
                <LegendItem
                    title={t('pension-cocktail-fill-screen-surplus')}
                    amount={-goalDelta}
                    className="mb-5"
                    color="bg-bnd-green"
                    generalPM={generalPM}
                    notes={[]}
                />
            )}
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-other-income')}
                amount={otherIncomes}
                className="mb-5"
                color="bg-bnd-pink"
                generalPM={generalPM}
                notes={[]}
            />
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-supplement')}
                amount={supplements / SUPPLEMENT_PER_MONTH_DIVIDER}
                className="mb-5"
                color="bg-bnd-cyan-1"
                generalPM={generalPM}
                notes={[
                    {
                        text: `${t('pension-cocktail-fill-screen-cocktail-legend-total')}: ${toPrice(supplements, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        })}`,
                    },
                ]}
            />
            {
                <LegendItem
                    title={t('pension-cocktail-fill-screen-cocktail-legend-savings-and-investments')}
                    amount={(savingsResultPageRes + investmentsResultPageRes) / SUPPLEMENT_PER_MONTH_DIVIDER}
                    className="mb-5"
                    color="bg-bnd-cyan-2"
                    generalPM={generalPM}
                    notes={[
                        {
                            text: `${t('pension-cocktail-fill-screen-cocktail-legend-total')}: ${toPrice(savingsResultPageRes + investmentsResultPageRes, {
                                onlyFullEuros: true,
                                withCurrencySymbol: true,
                            })}`,
                        },
                    ]}
                />
            }
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-employee-pension')}
                amount={totalEmployeePension}
                className="mb-5"
                color="bg-bnd-blue"
                generalPM={generalPM}
                notes={
                    isPensionCalculatedTogether(personalInfo)
                        ? [
                              {
                                  text: `${t('general-your')} pensioen`,
                                  amount: pension(pensionInfo, personalInfo, general.taxPercentage ?? 0),
                              },
                              {
                                  text: `${t('general-partner')} pensioen`,
                                  amount: pension(pensionInfo, personalInfo, general.taxPercentage ?? 0, true),
                              },
                          ]
                        : []
                }
            />
            <LegendItem
                title={t('pension-cocktail-fill-screen-cocktail-legend-aow')}
                amount={totalAOW}
                className="mb-5"
                color="bg-bnd-yellow"
                generalPM={generalPM}
                notes={
                    isPensionCalculatedTogether(personalInfo)
                        ? [
                              {
                                  text: `${t('general-your')} ${t('pension-cocktail-fill-screen-cocktail-legend-aow')}`,
                                  amount: aow(pensionInfo, personalInfo, general.taxPercentage ?? 0),
                              },
                              {
                                  text: `${t('general-partner')} ${t(
                                      'pension-cocktail-fill-screen-cocktail-legend-aow',
                                  )}`,
                                  amount: aow(pensionInfo, personalInfo, general.taxPercentage ?? 0, true),
                              },
                          ]
                        : []
                }
            />
        </div>
    );
};

const LegendItemDouble: React.FC<
    {
        title: string;
        colorLeft: string;
        colorRight: string;
    } & HTMLProps<HTMLDivElement>
> = ({ title, colorLeft, colorRight, className }) => {
    return (
        <div className={className}>
            <div className="col-span-5 flex items-center">
                <div
                    className={`rounded-full w-2 h-4 ${colorLeft}`}
                    style={{ borderBottomRightRadius: '100%', borderTopRightRadius: '100%' }}
                />
                <div
                    className={`rounded-full w-2 h-4 ${colorRight}`}
                    style={{ borderBottomLeftRadius: '100%', borderTopLeftRadius: '100%' }}
                />
                <div className="ml-4 flex-1">{title}</div>
            </div>
        </div>
    );
};

const LegendItem: React.FC<
    {
        title: string;
        amount: number;
        color: string;
        colorText?: string;
        generalPM: string;
        notes: { text: string; amount?: number }[];
    } & HTMLProps<HTMLDivElement>
> = ({ title, amount, color, generalPM, notes, colorText, className }) => {
    return (
        <div className={className}>
            <div className="flex items-center">
                <div className={`rounded-full w-4 h-4 ${amount > 0 ? 'bg-white' : color}`} />
                <div className="ml-4 flex-1 font-bold">{title}</div>
            </div>

            {amount > 0 && (
                <>
                    <div className="flex items-center">
                        <div className={`rounded-full w-4 h-4 ${color}`} />
                        <div className={`ml-4 flex-1 font-bold text-sm sm:text-lg ${colorText}`}>
                            {toPrice(+amount, { onlyFullEuros: true, withCurrencySymbol: true }) + ' ' + generalPM}
                        </div>
                    </div>

                    {notes.map((note, i) => {
                        return (
                            <div className="flex items-center" key={`note-${i}`}>
                                <div className="w-4 h-4 bg-white" />
                                <div className="ml-4 flex-1 opacity-50">{note.text}</div>
                                {note.amount !== undefined && (
                                    <div className="ml-4 flex-1 opacity-50">
                                        {+note.amount > 0
                                            ? toPrice(+note.amount, { onlyFullEuros: true, withCurrencySymbol: true })
                                            : '-'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};

const CocktailAxis: React.FC<{ graphLimit: number; axesCount: number }> = ({ graphLimit, axesCount }) => {
    return (
        <div className="col-span-3 h-full py-3">
            <div className="h-full flex flex-col flex-1 opacity-60">
                {Array.from(Array(axesCount - 1).keys()).map((_, i) =>
                    i === 0 ? (
                        <div
                            key={i}
                            className="flex flex-col justify-between flex-1"
                            style={{ bottom: `${(100 / axesCount) * i * 100}` }}
                        >
                            <div className="flex flex-row items-start">
                                <div className="w-6 mr-2 border-b-2 border-bnd-grey-3 border-dashed" />
                                <div className="relative">
                                    <span className="text-bnd-grey-3 absolute -top-2.5">
                                        {toPrice(graphLimit, {
                                            onlyFullEuros: true,
                                            withCurrencySymbol: true,
                                        })}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-row items-end">
                                <div className="w-6 mr-2 border-b-2 border-bnd-grey-3 border-dashed" />
                                <div className="relative">
                                    <span className="text-bnd-grey-3 absolute -bottom-2.5">
                                        {toPrice(graphLimit - (graphLimit * (i + 1)) / (axesCount - 1), {
                                            onlyFullEuros: true,
                                            withCurrencySymbol: true,
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            key={i}
                            className="flex flex-row items-end flex-1"
                            style={{ bottom: `${(100 / axesCount) * i * 100}` }}
                        >
                            <div className="w-6 mr-2 border-b-2 border-bnd-grey-3 border-dashed" />
                            <div className="relative">
                                <span className="text-bnd-grey-3 absolute -bottom-2.5">
                                    {toPrice(graphLimit - (graphLimit * (i + 1)) / (axesCount - 1), {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    })}
                                </span>
                            </div>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};


const ProgressBadge: React.FC<{ goal?: number; total: number; isColorGoal: boolean } & HTMLProps<HTMLDivElement>> = ({
    goal,
    total,
    isColorGoal,
    className,
}) => {
    const { t } = useTranslation('pension');
    const goalValue = goal ?? 0;
    return (
        <div className={`${className} grid grid-cols-7 md:grid-cols-9 xl:grid-cols-12 grid-rows-2 px-2 sm:px-8 pb-2 w-full xl:bg-gray-100 dark:bg-bnd-black-2 
            dark:border dark:border-gray-500 dark:shadow-none shadow-b-lg rounded-b-lg`}
        >
            <div className='flex items-end justify-around col-span-3'>
                <span className="opacity-50 text-center">
                    {t('pension-cocktail-fill-screen-total')}
                </span>
            </div>
            <div className='flex items-end justify-around col-start-5 col-span-3'>
                <span className="opacity-50 text-center">
                    {t('pension-cocktail-fill-screen-needed')}
                </span>
            </div>
            <span
                className={`font-semibold text-xl sm:text-2xl text-center col-start-1 col-span-3 ${isColorGoal 
                    ? (goalValue - total > 0 
                        ? 'text-bnd-orange' 
                        : 'text-bnd-green') 
                    : ''
                }`}
            >
                {total > 0 ? toPrice(total, { onlyFullEuros: true, withCurrencySymbol: true }) : '-'}
            </span>
            <span className="opacity-50 text-3xl text-center" >
                    /
                </span>
            <span className="font-semibold text-xl sm:text-2xl text-center col-span-3">
                {goalValue > 0 ? toPrice(goalValue, { onlyFullEuros: true, withCurrencySymbol: true }) : '-'}
            </span>
        </div>
    );
};

const GoalIndication: React.FC<{ total: number; goal: number; graphLimit: number; isColorGoal: boolean }> = ({
    goal,
    total,
    graphLimit,
    isColorGoal,
}) => {
    const { t } = useTranslation('pension');
    return (
        <div className="absolute w-full h-full flex flex-row py-3">
            <div className="w-full h-full flex flex-col flex-1">
                <div style={{ flex: 10000 - (goal / graphLimit) * 10000 }}></div>
                <div className="flex-1 grid grid-cols-8">
                    <div
                        className={`xl:col-span-6 border-b-2 ml-1 border-dashed ${
                            isColorGoal
                                ? `${total < goal
                                    ? 'border-bnd-red'
                                    : 'border-bnd-green'} col-span-5`
                                : 'border-gray-100 col-span-2 md:col-span-3'
                        }`}
                    ></div>
                    <div className={`relative ${isColorGoal 
                        ? 'col-start-4 sm:col-start-5 xl:col-start-6 col-span-3' 
                        : 'col-start-2 lg:col-start-3 xl:col-start-5 col-span-7 xl:col-span-5'}`}
                    >
                        <div
                            className={`absolute -top-3.5 rounded-lg px-2 ml-2 ${isColorGoal 
                                ? `${total < goal ? 'bg-bnd-red' : 'bg-bnd-green'} text-white px-5`
                                : 'bg-gray-100 text-black 2xl:ml-3'
                            }`}
                        >
                            {`${!isColorGoal 
                                    ? `${t('pension-cocktail-fill-screen-cocktail-axis-goal-text')}` 
                                    : ''}
                                ${toPrice(goal ?? 0, { onlyFullEuros: true, withCurrencySymbol: true })}`
                            }
                        </div>
                    </div>
                </div>
                <div style={{ flex: 10000 - (1 - goal / graphLimit) * 10000 - (goal === 0 ? 0 : 1) }}></div>
            </div>
        </div>
    );
};


export function supplement(supplementInfo: PensionParameters['supplementInfo'], type: AccountType, isPartner: boolean): number {
    const accountsTotal = (isPartner 
        ? supplementInfo?.partner?.accounts 
        : supplementInfo?.self?.accounts) ?? [] as Account[];

    if (!!accountsTotal){
        if (accountsTotal?.length === 0 || !(isPartner ? supplementInfo?.partner?.accountsAvailable : supplementInfo?.self?.accountsAvailable))
            return 0;
    }

    return accountsTotal.filter((a) => a.type === type).reduce((a, v) => (a += v.predictedAmount ?? 0), 0) ?? 0;
}

export function aow(
    pension: PensionParameters['pensionInfo'],
    info: PensionParameters['personalInfo'],
    taxPercentage: number,
    isPartner = false,
): number {
    if (!isPensionCalculatedTogether(info) && isPartner) {
        return 0;
    }

    let value = 0;
    if (isPartner) {
        value = pension?.partner?.aowGrossYearly ?? 0;
    } else if (isPensionCalculatedTogether(info)) {
        value = pension?.self?.aowGrossYearly?.together ?? 0;
    } else {
        value = pension?.self?.aowGrossYearly?.single ?? 0;
    }

    return toNetInCentsMonthly(value, taxPercentage);
}

export function pension(
    pension: PensionParameters['pensionInfo'],
    info: PensionParameters['personalInfo'],
    taxPercentage: number,
    isPartner = false,
): number {
    if (!isPensionCalculatedTogether(info) && isPartner) {
        return 0;
    }

    let value = 0;
    if (isPartner) {
        value = pension?.partner?.employeePensionGrossYearly ?? 0;
    } else {
        value = pension?.self?.employeePensionGrossYearly ?? 0;
    }

    return toNetInCentsMonthly(value, taxPercentage);
}

export function savingsResultPage(
    supplementInfo: PensionParameters['supplementInfo'],
): number {
    return supplementInfo?.self?.additionalSavingAccount?.account.predictedAmount ?? 0;
}

export function investmentsResultPage(
    supplementInfo: PensionParameters['supplementInfo'],
): number {
    return supplementInfo?.self?.additionalInvestmentAccount?.account.predictedAmount ?? 0;
}

export function yearSpacePensionAmount(
    supplementInfo: PensionParameters['supplementInfo'],
): number {
    return supplementInfo?.yearSpacePensionAccount?.predictedAmount ?? 0;
}

/**
 * Changes a yearly gross value to a monthly net value
 * @param value gross value in euros
 * @param taxPercentage The percentage as number e.g. 75
 * @returns The net amount in cents
 */
function toNetInCentsMonthly(value: number, taxPercentage: number): number {
    return Math.floor((value * (1 - taxPercentage / 100)) / 12 / 100) * 100;
}
